/**
 * Copy and FAQ generation for the SEO pages. Kept separate from the view so
 * the same strings feed both the visible page and the JSON-LD (page title,
 * meta description, FAQPage). All copy is Swedish to match the audience.
 */

// Human label for a year span, e.g. "2017–" or "2013–2017".
const yearLabel = fitment => {
  const { yearFrom, yearTo } = fitment || {};
  if (yearFrom && yearTo) return `${yearFrom}–${yearTo}`;
  if (yearFrom) return `${yearFrom}–`;
  if (yearTo) return `–${yearTo}`;
  return '';
};

const PAGE_TYPE_LABELS = {
  tire: { noun: 'Däck', nounLower: 'däck', segment: 'dack', segmentLabel: 'Däck' },
  rim: { noun: 'Fälgar', nounLower: 'fälgar', segment: 'falgar', segmentLabel: 'Fälgar' },
  /* P25009-408 — the answer pages. A "hjulpaket" is a complete wheel: a tyre
   * already mounted and balanced on a rim, sold as a set of four. */
  package: {
    noun: 'Hjulpaket',
    nounLower: 'hjulpaket',
    segment: 'hjulpaket',
    segmentLabel: 'Hjulpaket',
  },
};

export const pageTypeLabel = pageType => PAGE_TYPE_LABELS[pageType] || PAGE_TYPE_LABELS.tire;

/**
 * The rim diameters a car takes, in inches.
 *
 * `rims.all.optimal` is NOT a number, despite the name. The car-database import
 * writes the full rim specifications as one string — "8JX18 | 8JX19 | 8.5JX20 |
 * 8.5JX21 | 9JX22" — where each entry is width J x diameter. Appending an inch
 * mark to that produces `8JX18 | … | 9JX22"`, which is what the page showed
 * before this was noticed.
 *
 * So the diameters are pulled out and given as a range. A plain number is
 * accepted too, because nothing guarantees every row is in the same format.
 *
 * @param {Object} fitment
 * @returns {String} '18–22"', '19"', or '' when there is nothing usable
 */
export const rimDiameterLabel = fitment => {
  const raw = fitment?.rims?.all?.optimal;
  if (raw == null || raw === '') return '';

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return `${raw}"`;
  }

  /* Each entry looks like "8.5JX20": the number after the X is the diameter.
   * A road car diameter is two digits, which keeps a width like 8.5 out. */
  const diameters = [...new Set((String(raw).match(/x\s*(\d{2})\b/gi) || []).map(m => Number(m.replace(/[^\d]/g, ''))))]
    .filter(n => n >= 10 && n <= 30)
    .sort((a, b) => a - b);

  if (diameters.length === 0) {
    // An unrecognised format is left alone rather than decorated with an inch
    // mark it may not deserve.
    return String(raw).trim();
  }
  return diameters.length === 1
    ? `${diameters[0]}"`
    : `${diameters[0]}–${diameters[diameters.length - 1]}"`;
};

/* The offsets a car's front axle takes, as one string. Used by the answer
 * page's fit table — offset is one of the three numbers that decides whether a
 * wheel physically fits, and it is the one buyers most often do not know. */
const offsetRange = fitment => {
  const front = fitment?.offsets?.front || {};
  const min = front.min ?? front.optimal;
  const max = front.max ?? front.optimal;
  if (min == null && max == null) return '';
  if (min != null && max != null && min !== max) return `ET${min}–ET${max}`;
  return `ET${min ?? max}`;
};

/**
 * The fit facts of a car, as ordered rows for a real table (P25009-408 BR-3).
 *
 * These come from the car database, not from a listing, so they are the same
 * whichever package a buyer ends up looking at — which is exactly what makes
 * them worth stating as facts on an answer page.
 *
 * Rows with no answer are dropped. Hub bore in particular is missing for a lot
 * of models, and a labelled blank reads as "no hub bore", which is impossible.
 *
 * @param {Object} fitment payload from /api/seo/fitment
 * @returns {Array<{key: String, label: String, value: String}>}
 */
export const buildFitTable = fitment => {
  const tireSizes = (fitment?.tires?.all || []).slice(0, 4).join(', ');
  const rows = [
    ['tires', 'Däckdimensioner', tireSizes],
    ['rim', 'Fälgdiameter', rimDiameterLabel(fitment)],
    ['boltPattern', 'Bultmönster', fitment?.boltPattern || ''],
    ['centerBore', 'Navhål (CB)', fitment?.centerBoreMm ? `${fitment.centerBoreMm} mm` : ''],
    ['offset', 'Offset (ET)', offsetRange(fitment)],
    ['studThread', 'Bultgänga', fitment?.studThread || ''],
    ['years', 'Årsmodeller', yearLabel(fitment)],
  ];

  return rows
    .filter(([, , value]) => value !== '' && value != null)
    .map(([key, label, value]) => ({ key, label, value: String(value) }));
};

/**
 * The direct-answer paragraph (P25009-408 BR-3 / AC-3).
 *
 * The first thing on the page and the sentence an answer engine is meant to
 * lift: what fits, how many are for sale right now, and from what price. The
 * live figures are passed in rather than derived, and the sentence leaves out
 * whichever of them is not known instead of writing a zero.
 *
 * @param {Object} fitment payload from /api/seo/fitment
 * @param {Object} [live]
 * @param {Number} [live.packageCount] packages currently for sale
 * @param {Boolean} [live.countIsAtLeast] the count is a floor, not an exact
 *   figure (the scan hit its page ceiling), so it is written as "50+"
 * @param {String} [live.fromPrice] formatted lowest price, e.g. "12 900 kr"
 * @returns {String}
 */
export const buildAnswerParagraph = (
  fitment,
  { packageCount, countIsAtLeast, fromPrice } = {}
) => {
  const { make, model, boltPattern, centerBoreMm } = fitment || {};
  const years = yearLabel(fitment);
  const tireSizes = (fitment?.tires?.all || []).slice(0, 3).join(', ');
  const rimDiameters = rimDiameterLabel(fitment);

  const car = `${make} ${model}${years ? ` (${years})` : ''}`;
  const sentences = [
    `Ett komplett hjulpaket till ${car} består av fyra fälgar med däck monterade och balanserade, klara att skruva på.`,
  ];

  const fit = [
    tireSizes ? `däckdimension ${tireSizes}` : '',
    rimDiameters ? `fälgdiameter ${rimDiameters}` : '',
    boltPattern ? `bultmönster ${boltPattern}` : '',
    centerBoreMm ? `navhål ${centerBoreMm} mm` : '',
    offsetRange(fitment) ? `offset ${offsetRange(fitment)}` : '',
  ].filter(Boolean);
  if (fit.length) {
    sentences.push(`${make} ${model} tar ${fit.join(', ')}.`);
  }

  /* The live half. Written only when we have a figure: "0 paket från 0 kr" is
   * both useless and, on a page that exists to be quoted, actively harmful. */
  if (packageCount > 0) {
    const countPhrase =
      packageCount === 1 && !countIsAtLeast
        ? 'Just nu finns 1 hjulpaket till salu'
        : `Just nu finns ${packageCount}${countIsAtLeast ? '+' : ''} hjulpaket till salu`;
    sentences.push(fromPrice ? `${countPhrase}, från ${fromPrice}.` : `${countPhrase}.`);
  } else {
    sentences.push(
      `Just nu finns inga färdiga hjulpaket till ${make} ${model} – men däck och fälgar som passar finns bland annonserna, och du kan sätta ihop ett paket själv.`
    );
  }

  return sentences.join(' ');
};

// SEO page title, e.g. "Däck till Volvo XC60 (2017–) – storlek & priser | Wheelplace".
export const buildTitle = (pageType, fitment, marketplaceName) => {
  const { noun } = pageTypeLabel(pageType);
  const years = yearLabel(fitment);
  const yearsSuffix = years ? ` (${years})` : '';
  return `${noun} till ${fitment.make} ${fitment.model}${yearsSuffix} – storlek & priser | ${marketplaceName}`;
};

// Meta description.
export const buildDescription = (pageType, fitment) => {
  const { nounLower } = pageTypeLabel(pageType);
  // Rim pages state a diameter, tyre pages state tyre sizes. Both go through
  // rimDiameterLabel / tires.all rather than printing `rims.all.optimal` raw.
  const dims =
    pageType === 'rim'
      ? rimDiameterLabel(fitment)
      : (fitment.tires?.all || []).slice(0, 3).join(', ');
  const dimsPart = dims ? ` Rekommenderade dimensioner: ${dims}.` : '';

  if (pageType === 'package') {
    // A package has both a tyre size and a rim diameter, so it states both.
    const tires = (fitment.tires?.all || []).slice(0, 3).join(', ');
    const tirePart = tires ? ` Rekommenderade dimensioner: ${tires}.` : '';
    const rimDiameters = rimDiameterLabel(fitment);
    const rimPart = rimDiameters ? ` Fälg ${rimDiameters}.` : '';
    const bolts = fitment.boltPattern ? ` Bultmönster ${fitment.boltPattern}.` : '';
    return `Kompletta hjulpaket till ${fitment.make} ${fitment.model} – fälg och däck monterat och balanserat. Se vilken storlek som passar, aktuella priser och paket som finns i lager nu.${tirePart}${rimPart}${bolts}`;
  }

  return `Hitta ${nounLower} till ${fitment.make} ${fitment.model}. Se rätt storlek, bultmönster och offset – och jämför aktuella annonser från säljare i hela Sverige.${dimsPart}`;
};

// H1.
export const buildHeading = (pageType, fitment) => {
  const { noun } = pageTypeLabel(pageType);
  const years = yearLabel(fitment);
  const yearsSuffix = years ? ` ${years}` : '';
  return `${noun} till ${fitment.make} ${fitment.model}${yearsSuffix}`;
};

// Unique, per-car body copy (a few paragraphs). Varies by make/model/years and
// the actual fitment data so every page carries distinct, indexable prose
// rather than a boilerplate template.
export const buildBodyText = (pageType, fitment) => {
  const { make, model, boltPattern, centerBoreMm } = fitment;
  const { noun, nounLower } = pageTypeLabel(pageType);
  const years = yearLabel(fitment);
  const yearsPhrase = years ? ` (${years})` : '';
  const tireDims = (fitment.tires?.all || []).slice(0, 4);
  // Never the raw `rims.all.optimal` — see rimDiameterLabel.
  const rimOptimal = rimDiameterLabel(fitment);

  const paragraphs = [];

  paragraphs.push(
    `Letar du efter ${nounLower} till din ${make} ${model}${yearsPhrase}? På den här sidan har vi samlat allt du behöver veta för att hitta rätt – från originaldimensioner och bultmönster till aktuella annonser från säljare i hela Sverige. ${make} ${model} är en populär bil på den svenska marknaden, och rätt ${nounLower} gör stor skillnad för både komfort, säkerhet och utseende.`
  );

  if (pageType === 'package') {
    paragraphs.push(
      `Ett hjulpaket är fälg och däck som köps ihop, redan monterade och balanserade. Det som skiljer ett paket från att köpa fälgar och däck var för sig är att passformen redan är kontrollerad: fälgens bultmönster, navhål och offset stämmer med bilen, och däcket har rätt dimension för fälgen. Du behöver inte räkna ut något själv.`
    );
    paragraphs.push(
      tireDims.length > 0
        ? `För ${make} ${model} bygger paketen på dimensionerna ${tireDims.join(
            ', '
          )}. Vilken av dem som passar din bil beror på årsmodell och motoralternativ, så slå registreringsnummret i sökrutan om du vill vara säker – då matchar vi mot din bils egna specifikation i stället för mot modellen i stort.`
        : `Vilken dimension som passar din ${make} ${model} beror på årsmodell och motoralternativ. Slå registreringsnummret i sökrutan så matchar vi mot din bils egna specifikation.`
    );
  } else if (pageType === 'tire') {
    paragraphs.push(
      tireDims.length > 0
        ? `Vanliga däckdimensioner för ${make} ${model} är ${tireDims.join(
            ', '
          )}. Vilken storlek som passar just din bil beror på årsmodell, motoralternativ och fälgstorlek. Slå ditt registreringsnummer i sökrutan så matchar vi exakt rätt däck mot din bils specifikation – du slipper gissa och hamnar direkt på rätt annonser.`
        : `Vilken däckstorlek som passar din ${make} ${model} beror på årsmodell och fälgstorlek. Slå ditt registreringsnummer i sökrutan så matchar vi rätt däck mot din bils specifikation.`
    );
  } else {
    paragraphs.push(
      rimOptimal
        ? `Rekommenderad fälgstorlek för ${make} ${model} ligger kring ${rimOptimal}. Vid byte av fälg är det viktigt att bultmönster, centrumhål (CB) och offset (ET) stämmer med din bil – annars passar inte fälgen. Slå ditt registreringsnummer så visar vi vilka fälgar som passar och tar dig vidare till rätt annonser.`
        : `Vid byte av fälg på ${make} ${model} är det viktigt att bultmönster, centrumhål och offset stämmer. Slå ditt registreringsnummer så visar vi vilka fälgar som passar.`
    );
  }

  if (boltPattern) {
    paragraphs.push(
      `${make} ${model} har bultmönster ${boltPattern}${
        centerBoreMm ? ` och centrumhål (CB) på ${centerBoreMm} mm` : ''
      }. Dessa mått avgör vilka ${nounLower} och fälgar som är kompatibla med bilen. Med rätt mått kan du tryggt jämföra både nya och begagnade ${noun.toLowerCase()} bland annonserna nedan.`
    );
  }

  return paragraphs;
};

// Short FAQ tailored to the car. The same array drives the FAQPage JSON-LD.
export const buildFaqs = (pageType, fitment) => {
  const { make, model, boltPattern, centerBoreMm } = fitment;
  const tireDims = (fitment.tires?.all || []).slice(0, 4).join(', ');
  /* Already inch-marked by rimDiameterLabel — the answers below must not add a
   * second one, which is what happened when this read `rims.all.optimal` raw. */
  const rimOptimal = rimDiameterLabel(fitment);
  const faqs = [];

  /* P25009-408 BR-3 asks for a question-and-answer block on the answer pages.
   * These are the questions the search log shows people actually ask about a
   * package, in the order they ask them, and every answer is a fact from the
   * car database rather than sales copy. */
  if (pageType === 'package') {
    faqs.push({
      question: `Vilket hjulpaket passar en ${make} ${model}?`,
      answer: tireDims
        ? `Ett hjulpaket till ${make} ${model} ska ha dimension ${tireDims}${
            boltPattern ? ` och bultmönster ${boltPattern}` : ''
          }. Slå registreringsnummret ovan så visar vi paketen som passar just din årsmodell.`
        : `Slå registreringsnummret ovan så visar vi vilka hjulpaket som passar just din ${make} ${model}.`,
    });
    faqs.push({
      question: 'Är däcken monterade på fälgarna?',
      answer:
        'Ja. Ett hjulpaket säljs med däcken monterade och balanserade på fälgarna, som fyra färdiga hjul. Du behöver inte boka någon montering för att få ihop hjulet – bara skruva fast det.',
    });
    if (boltPattern) {
      faqs.push({
        question: `Vilket bultmönster har ${make} ${model}?`,
        answer: `${make} ${model} har bultmönster ${boltPattern}${
          centerBoreMm ? ` och centrumhål (CB) ${centerBoreMm} mm` : ''
        }. Båda måtten måste stämma för att fälgen ska sitta rätt på navet.`,
      });
    }
    faqs.push({
      question: `Vad kostar ett hjulpaket till ${make} ${model}?`,
      answer: `Priset beror på fälg, däckmärke och skick. Priserna på den här sidan är hämtade från annonserna som ligger ute just nu, så de är aktuella – det billigaste paketet står först.`,
    });
  } else if (pageType === 'tire') {
    faqs.push({
      question: `Vilken däckstorlek passar en ${make} ${model}?`,
      answer: tireDims
        ? `Vanliga originaldimensioner för ${make} ${model} är ${tireDims}. Slå ditt registreringsnummer ovan så visar vi exakt vilka däck som passar just din bil.`
        : `Slå ditt registreringsnummer ovan så visar vi exakt vilka däck som passar just din ${make} ${model}.`,
    });
  } else {
    faqs.push({
      question: `Vilken fälgstorlek passar en ${make} ${model}?`,
      answer: rimOptimal
        ? `Rekommenderad fälgstorlek för ${make} ${model} är ${rimOptimal}. Slå ditt registreringsnummer ovan så visar vi vilka fälgar som passar din bil.`
        : `Slå ditt registreringsnummer ovan så visar vi vilka fälgar som passar din ${make} ${model}.`,
    });
  }

  // The package branch above already asks this one, with a fuller answer.
  if (boltPattern && pageType !== 'package') {
    faqs.push({
      question: `Vilket bultmönster har ${make} ${model}?`,
      answer: `${make} ${model} har bultmönster ${boltPattern}${
        centerBoreMm ? ` och centrumhål (CB) ${centerBoreMm} mm` : ''
      }.`,
    });
  }

  faqs.push({
    question: `Var köper jag ${pageTypeLabel(pageType).nounLower} till ${make} ${model}?`,
    answer: `På Wheelplace hittar du aktuella annonser från säljare i hela Sverige. Slå registreringsnummer eller bläddra bland annonserna nedan för att se alla alternativ som passar din ${make} ${model}.`,
  });

  return faqs;
};
